if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      auBnplBootstrapInterval = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.setupAuBnplCard();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        if (this.auBnplBootstrapInterval) {
          window.clearInterval(this.auBnplBootstrapInterval);
          this.auBnplBootstrapInterval = undefined;
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("modals");
          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          this.productForm?.toggleSubmitButton(
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true,
            window.variantStrings.soldOut,
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.setupAuBnplCard();
        };
      }

      shouldUseAuBnplCard() {
        return (
          window.location.hostname.includes("speediance.com.au") &&
          Boolean(
            this.querySelector(
              `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
            ) || this.querySelector("[data-bnpl-card]"),
          )
        );
      }

      getAuBnplPriceAmount() {
        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return null;

        const priceElement = [
          ".price__sale .price-item--sale",
          ".price__sale .price-item--last",
          ".price-item--sale",
          ".price__regular .price-item--regular",
          ".price-item--regular",
        ]
          .map((selector) => priceRoot.querySelector(selector))
          .find((node) => node && node.textContent.trim().length);

        const raw = (priceElement?.textContent || priceRoot.textContent || "")
          .replace(/[^0-9,.\-]/g, "");
        if (!raw) return null;

        const lastComma = raw.lastIndexOf(",");
        const lastDot = raw.lastIndexOf(".");
        const normalized =
          lastComma > lastDot
            ? raw.replace(/\./g, "").replace(",", ".")
            : raw.replace(/,/g, "");

        const amount = Number.parseFloat(normalized);
        return Number.isFinite(amount) ? amount : null;
      }

      formatAuBnplMoney(value, currencyCode) {
        try {
          return new Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(value);
        } catch (error) {
          return `${currencyCode} ${value.toFixed(2)}`;
        }
      }

      ensureAuBnplStyles() {
        if (document.getElementById("au-bnpl-card-styles")) return;

        const style = document.createElement("style");
        style.id = "au-bnpl-card-styles";
        style.textContent = `
/* AU product-page finance line - Humm only, inline (2026-08-14).
   Splitit was removed from the AU storefront; the old two-up .bnpl-card /
   .bnpl-grid / .bnpl-plan / .bnpl-splitit* markup went with it. Do not
   reintroduce those classes. The official humm-au.com widget is still mounted
   but visually hidden: it is the regulated disclosure artifact and owns the
   real "Learn more" plan modal, while the numbers on screen come from Humm's
   own pricing API so the whole thing fits on one line under the price. */
.bnpl-inline {
  font-family: 'Helvetica Now Var', 'Helvetica Now', Helvetica, Arial, sans-serif;
  position: relative;
  margin: 0.6rem 0 0;
  max-width: 56rem;
}
.bnpl-inline[hidden] { display: none !important; }
.bnpl-inline__line {
  margin: 0;
  font-size: 1.4rem;
  line-height: 1.45;
  color: #0A0A0A;
}
.bnpl-inline__main { font-weight: 500; }
.bnpl-inline__amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.bnpl-inline__per { font-weight: 700; }
.bnpl-inline__brand {
  font-weight: 800;
  letter-spacing: 0.01em;
  color: #FF5733;
  white-space: nowrap;
}
.bnpl-inline__note {
  display: block;
  margin-top: 0.15rem;
  color: rgba(10, 10, 10, 0.68);
  font-size: 1.2rem;
}
.bnpl-inline__learn {
  appearance: none;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 1.2rem;
  font-weight: 600;
  color: #FF5733;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
  cursor: pointer;
}
.bnpl-inline__learn:hover { color: #cc4529; }
.bnpl-inline__learn:focus-visible {
  outline: 0.2rem solid rgba(10, 10, 10, 0.55);
  outline-offset: 0.2rem;
  border-radius: 0.3rem;
}
.bnpl-humm-sdk {
  position: absolute;
  width: 0.1rem;
  height: 0.1rem;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
}
@media (max-width: 749px) {
  .bnpl-inline { max-width: 100%; }
  .bnpl-inline__line { font-size: 1.3rem; }
}
        `;
        document.head.appendChild(style);
      }

      ensureAuBnplMarkup() {
        const existing = this.querySelector(
          `[data-bnpl-card][data-section-id="${this.dataset.section}"]`,
        );
        const compactMarkup = `
          <p class="bnpl-inline__line" data-bnpl-row="humm">
            <span class="bnpl-inline__main">or
              <span class="bnpl-inline__amount" data-humm-instalment>$--.--</span><span class="bnpl-inline__per">/fortnight</span>
              over <span data-humm-months>30</span> months with <span class="bnpl-inline__brand">Humm</span></span>
            <span class="bnpl-inline__note"><span data-humm-deposit>20%</span> deposit &middot; approval &amp; fees apply &middot; <button type="button" class="bnpl-inline__learn" data-humm-learn>Learn more</button></span>
          </p>
          <div class="bnpl-humm-sdk" data-humm-target aria-hidden="true"></div>
        `;

        if (existing) {
          if (
            !existing.querySelector("[data-humm-instalment]") ||
            !existing.querySelector("[data-humm-target]")
          ) {
            existing.className = "bnpl-inline";
            existing.hidden = true;
            existing.innerHTML = compactMarkup;
          }
          return existing;
        }

        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceRoot) return null;

        const card = document.createElement("div");
        card.className = "bnpl-inline";
        card.hidden = true;
        card.dataset.bnplCard = "true";
        card.dataset.sectionId = this.dataset.section;
        card.dataset.currencyCode =
          window?.Shopify?.currency?.active || "AUD";
        card.innerHTML = compactMarkup;

        priceRoot.insertAdjacentElement("afterend", card);
        return card;
      }

      auHummIsViable(amount) {
        // Humm AU account 30146926 exposes a single 65-fortnight (30-month)
        // plan, and it is usable only inside a band:
        //   floor:  it adds a flat ~$358.50 in fees ($60 establishment +
        //            $9.95/month x 30) to ANY purchase, which turns "total
        //            payable" into nonsense on cheap items ($399 -> $757.50).
        //   ceiling:  maximumLoanAmount is 6000; above it Humm's pricing API
        //            returns no quote at all (e.g. the ~$6,999 GM2 2S variant).
        // Splitit used to cover both ends. It was removed 2026-08-14, so
        // outside this band we now hide the finance line entirely rather than
        // advertise a plan the customer cannot actually be approved for.
        const HUMM_MIN_AMOUNT = 1500;
        const HUMM_MAX_AMOUNT = 6000;
        return (
          Number.isFinite(amount) &&
          amount >= HUMM_MIN_AMOUNT &&
          amount <= HUMM_MAX_AMOUNT
        );
      }

      fetchAuHummQuote(amount) {
        // Humm's own pricing API - the same source the official widget reads,
        // so the inline numbers are the merchant's real plan terms rather than
        // our arithmetic. Cached per amount; any failure resolves to null and
        // the line stays hidden (fail closed).
        this.auHummQuotes = this.auHummQuotes || new Map();
        const key = String(amount);
        if (this.auHummQuotes.has(key)) return this.auHummQuotes.get(key);

        const request = fetch(
          `https://cart.shophumm.com.au/v1/widgets/pricing?purchaseAmount=${amount}&merchantId=30146926`,
        )
          .then((response) => (response.ok ? response.json() : null))
          .then((data) => {
            const quote = data && data.bestQuote;
            const instalment = quote && Number(quote.instalmentAmount);
            if (!Number.isFinite(instalment) || instalment <= 0) return null;
            return {
              instalment,
              fortnights: Number(quote.term) || 65,
              upFront: Number(quote.upFrontAmount) || 0,
            };
          })
          .catch(() => null);

        this.auHummQuotes.set(key, request);
        return request;
      }

      mountAuHummWidget(target, amount) {
        if (!target) return;

        if (
          target.dataset.hummAmount === String(amount) &&
          (target.querySelector(".humm-price-info-widget") ||
            target.querySelector("script"))
        ) {
          return;
        }

        target.innerHTML = "";
        target.dataset.hummAmount = String(amount);

        const script = document.createElement("script");
        script.src = `https://bpi.humm-au.com/au/content/scripts/price-info_sync.js?productPrice=${amount}&merchantId=30146926`;
        script.async = true;
        target.appendChild(script);
      }

      bindAuHummLearnMore(card) {
        const button = card.querySelector("[data-humm-learn]");
        if (!button || button.dataset.bound === "true") return;
        button.dataset.bound = "true";
        button.addEventListener("click", () => {
          // The hidden official widget owns Humm's real plan modal. Clicking
          // its trigger programmatically works even though it is not visible.
          const trigger = card.querySelector("a[data-remodal-target]");
          if (trigger) trigger.click();
        });
      }

      syncAuBnplCard() {
        if (!this.shouldUseAuBnplCard()) return false;

        this.ensureAuBnplStyles();
        const card = this.ensureAuBnplMarkup();
        if (!card) return false;

        const amount = this.getAuBnplPriceAmount();
        if (!amount) return false;

        // Idempotence guard - also re-runs when a section re-render swaps in a
        // fresh card carrying the liquid placeholders.
        if (card.dataset.bnplApplied === String(amount)) return true;
        card.dataset.bnplApplied = String(amount);

        if (!this.auHummIsViable(amount)) {
          card.hidden = true;
          const target = card.querySelector("[data-humm-target]");
          if (target) {
            target.innerHTML = "";
            delete target.dataset.hummAmount;
          }
          return true;
        }

        // Keep the regulated widget mounted (hidden) for its disclosure modal.
        this.mountAuHummWidget(card.querySelector("[data-humm-target]"), amount);
        this.bindAuHummLearnMore(card);

        const currencyCode = card.dataset.currencyCode || "AUD";
        Promise.resolve(this.fetchAuHummQuote(amount)).then((quote) => {
          // Bail if the shopper switched variant while the quote was in flight.
          if (card.dataset.bnplApplied !== String(amount)) return;

          if (!quote) {
            card.hidden = true;
            return;
          }

          const instalmentNode = card.querySelector("[data-humm-instalment]");
          const monthsNode = card.querySelector("[data-humm-months]");
          const depositNode = card.querySelector("[data-humm-deposit]");

          if (instalmentNode) {
            instalmentNode.textContent = this.formatAuBnplMoney(
              quote.instalment,
              currencyCode,
            );
          }
          if (monthsNode) {
            monthsNode.textContent = String(Math.round(quote.fortnights / 2.1667));
          }
          if (depositNode && quote.upFront > 0) {
            depositNode.textContent = `${Math.round((quote.upFront / amount) * 100)}%`;
          }

          card.hidden = false;
        });

        return true;
      }

      setupAuBnplCard() {
        if (!this.shouldUseAuBnplCard()) return;

        this.syncAuBnplCard();

        if (this.auBnplBootstrapInterval) {
          window.clearInterval(this.auBnplBootstrapInterval);
        }

        // The Version / Accessories tile pickers replace the price node without
        // firing an event we can rely on, so poll rather than cache references
        // (same pattern as the UK line and gm2-addons). syncAuBnplCard is
        // guarded per amount, so a steady tick costs nothing once settled.
        this.auBnplBootstrapInterval = window.setInterval(() => {
          if (!this.isConnected) {
            window.clearInterval(this.auBnplBootstrapInterval);
            this.auBnplBootstrapInterval = undefined;
            return;
          }
          this.syncAuBnplCard();
        }, 800);
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));
      }

      updateMedia(html, variantFeaturedMediaId) {
        // Filtered main slides, thumbnails and modal must change as one set.
        // Scope to this product section: other product/quick-add galleries stay intact.
        const gallery = this.querySelector('media-gallery[data-bm-variant-gallery]');
        const sourceSection = html.getElementById(`MainProduct-${this.sectionId}`) || html;
        const nextGallery = sourceSection.querySelector('media-gallery[data-bm-variant-gallery]');
        if (gallery && nextGallery) {
          const nextModal = html.getElementById(`ProductModal-${this.sectionId}`);
          const modalContent = this.productModal?.querySelector('.product-media-modal__content');
          const nextModalContent = nextModal?.querySelector('.product-media-modal__content');
          if (modalContent && nextModalContent) modalContent.innerHTML = nextModalContent.innerHTML;
          gallery.replaceWith(nextGallery);
          const firstMediaId = nextGallery.querySelector('[data-media-id]')?.dataset.mediaId;
          if (firstMediaId) nextGallery.setActiveMedia?.(firstMediaId, false);
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          return;
        }

        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        return fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === ".quantity__label") {
              const updatedAriaLabelledBy =
                updated.getAttribute("aria-labelledby");
              if (updatedAriaLabelledBy) {
                current.setAttribute("aria-labelledby", updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent =
                    updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}

